import React, { useEffect, useRef, useState } from 'react';
import { Alert, Col, Row, Button, Modal, Tooltip as AntTooltip } from 'antd';
import HTTPService from '../../utils/makerequest';
import './../../assets/sass/components/Dashboard.scss';
import { IPageData } from '../../interfaces/page-data';
import { t } from 'i18next';
import { StateLoader } from '../../redux/StateLoader';
import AltisDataGrid, { IAltisDataGridColumns } from '../../AltisComponent/AltisDataGrid';
import { Toast } from 'primereact/toast';
import { IAuthentication } from '../../interfaces/authentication';
import { IAssetLocation, IAssetLocations } from '../../interfaces/IAssetLocation';
import { ResponsivePie } from '@nivo/pie'
import {
  Card as MuiCard,
  CardContent,
  Typography,
  makeStyles,
  Box
} from '@material-ui/core';
import { AccountCircle } from '@material-ui/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell } from 'recharts';
import { IAssetCountings } from '../../interfaces/IAssetCounting';
import { EyeOutlined } from '@ant-design/icons';
import { ICampusData, ICampusFilter } from '../../interfaces/ICampusData';
import MUIDatagrid, { IMUIDataGridColumns } from '../../AltisComponent/MUIDataGrid';
import shape from '@material-ui/core/styles/shape';


interface IAsset {
  inventoryName?: string;
  locationId?: number;
}

interface ISelectedAssets {
  total: number;
  count?: number;
  data?: IAsset[];
}

const useStyles = makeStyles((theme) => ({
  card: {
    position: 'relative',
    overflow: 'visible',
    marginBottom: theme.spacing(2),
    height: '120px',
  },
  iconBox: {
    position: 'absolute',
    top: -25,
    left: 20,
    padding: theme.spacing(2),
    height: 90,
    width: 90,
    borderRadius: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  icon: {
    fontSize: 45,
    color: '#fff',
  },
  content: {
    paddingTop: theme.spacing(6),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    paddingRight: theme.spacing(3),
  },
  label: {
    color: '#999',
    fontSize: 16,
    fontWeight: 400,
    marginBottom: theme.spacing(1),
  },
  value: {
    color: '#3C4858',
    fontSize: '2em',
    fontWeight: 500,
    marginBottom: 0,
  }
}));

const Dashboard: React.FunctionComponent<any> = props => {
  const { onSetPage } = props;
  const [pieData, setPieData] = useState<any[]>([]);
  const [shapeData, setShapeData] = useState<any[]>([]);
  const [assetLocation, setAssetLocation] = useState<IAssetLocations>({});
  const [assetLocationDetail, setAssetLocationDetail] = useState<IAssetLocations>({});
  const [dashboardDetails, setDashboardDetails] = useState<any>({});
  const [allCounting, setAllCounting] = useState<IAssetCountings>({});
  const [categoryChartData, setCategoryChartData] = useState<any[]>([]); // chartData yerine categoryChartData
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState<ISelectedAssets>();
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [campusData, setCampusData] = useState<ICampusFilter>({});
  const loader = new StateLoader();
  const userInfo: IAuthentication = loader.loadAuthState();
  const classes = useStyles();
  const dataGridRef = useRef<any>(null);
  const toast = useRef<any>(null);

  const pageData: IPageData = {
    title: t('dashboard'),
    loaded: true
  };

  useEffect(() => {
    onSetPage(pageData);
    getWidgets();
    CampusFilter();
    getActiveCountings();
    AssetLocationFilter();
  }, []);

  const getWidgets = () => {
    HTTPService.GET(`/Dashboard/getDashboardCount`)
      .then(response => {
        if (!response.data) {
          return;
        }
        setDashboardDetails(response.data);
      })
      .catch(error => {
        console.error('Error fetching pie data:', error);
      });

    HTTPService.GET(`/Dashboard/allInventoryStatusCount`)
      .then(response => {
        if (!response.data) {
          return;
        }
        const pieChartData = response.data.map(item => ({
          id: item.inventoryStatusName,
          label: item.inventoryStatusName,
          value: Number(item.percentage).toFixed(0),
          formattedValue: `${Number(item.percentage).toFixed(0)}%`
        })) || [];

        setPieData(pieChartData);
      })
      .catch(error => {
        console.error('Error fetching pie data:', error);
      });

    HTTPService.GET(`/Dashboard/allInventoryCategoryCount`)
      .then(response => {
        if (!response.data) {
          return;
        }
        const totalCount = response.data.reduce((acc, item) => acc + item.count, 0);
        const formattedData = response.data.map(item => ({
          name: item.categoryName.length > 10 ? item.categoryName.slice(0, 15) + '...' : item.categoryName,
          value: item.count,
          fill: getRandomColor()
        }));

        setCategoryChartData(formattedData);
      })
      .catch(error => {
        console.error('Error fetching category data:', error);
      });
  };

  const AssetLocationFilter = (e?) => {
    const body = {
      pageSize: e?.pageSize || 10,
      page: e?.page || 1,
      companyId: userInfo?.companyId,
      campusName: e?.campusName,
      locationName: e?.locationName,
      assetCount: e?.assetCount
    }

    HTTPService.POST(`/Dashboard/assetbylocation`, body).then((response) => {
      if (!response.data) {
        return;
      }
      if (response.status === 200) {
        setAssetLocation(response.data)
        setAssetLocationDetail(response.data);
      } else {
      }
    });
  };

  const CampusFilter = (e?) => {
    HTTPService.POST(`/Campus/filters`, {}).then((response) => {
      if (!response.data) return;
      if (response.status === 200) {
        setCampusData(response.data);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: t('errors'),
          detail: t(response.data),
          life: 3000
        });
      }
    });
  };

  const combinedData = (allCounting?.data || []).flatMap(item => [
    { month: ` ${item.month} `, value: item.completeCount, type: `Tamamlanan Sayım ` },
    { month: ` ${item.month} `, value: item.totalCount - item.completeCount, type: ` Kalan Sayım ` },
  ]);

  const filteredData = combinedData.filter(item => item.value >= 0); // Negatif değerleri filtrele


  const staticShapeData = [
    { type: 'Kategori A', Kategori: 10 },
    { type: 'Kategori B', Kategori: 10 },
    { type: 'Kategori C', Kategori: 10 },
    { type: 'Kategori D', Kategori: 10 },
    { type: 'Kategori E', Kategori: 10 },
    { type: 'Kategori F', Kategori: 20 },
    { type: 'Kategori H', Kategori: 30 },
  ];


  const customShapeConfig = {
    data: shapeData.length > 0 ? shapeData : staticShapeData,
    xField: 'type',
    yField: 'Kategori',
    stack: true,
    colorField: 'type',
    //colorField:'green',
    label: {
      text: 'Kategori',
      textBaseline: 'bottom',
      position: 'top',
      style: {
        fontSize: 8,
        fontWeight: 'bold'
      }
    },
    interaction: {
      elementHighlightByColor: {
        link: true,
      },
    },
    legend: false,
    state: {
      active: { linkFill: 'rgba(0,0,0,0.25)', stroke: 'black', lineWidth: 0.5 },
      inactive: { opacity: 0.5 },
    },
  };

  const getRandomColor = () => {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  };

  const options = {
    responsive: true,
    legend: {
      display: false
    },
    tooltips: {
      enabled: false
    },
    scales: {
      xAxes: [
        {
          gridLines: {
            display: false,
          },
        },
      ],
      yAxes: [
        {
          gridLines: {
            display: false,
          },
          ticks: {
            display: true,
            font: {
              size: 10,
            },
            stepSize: 5,
          },
        },
      ],
    },
    onRender: (chart) => {
      const ctx = chart.ctx;
      ctx.save();
      ctx.font = 'bold 8px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      chart.data.datasets.forEach((dataset, i) => {
        const meta = chart.getDatasetMeta(i);
        meta.data.forEach((bar, index) => {
          const value = dataset.data[index];
          ctx.fillStyle = 'black';
          ctx.fillText(value, bar.x, bar.y - 5);
        });
      });
      ctx.restore();
    },
  };





  const getActiveCountings = () => {
    HTTPService.GET(`/Dashboard/inventoryByCounting`)
      .then(response => {
        if (!response.data) return;
        setAllCounting(response.data)
      })
      .catch(error => {
        console.error('Error fetching active countings:', error);
        toast.current?.show({
          severity: 'error',
          summary: t('errors'),
          detail: t('errorFetchingData'),
          life: 3000
        });
      });
  };


  const assetModalColumns: IMUIDataGridColumns[] = [
    {
      title: t('asset'),
      key: 'inventoryName',
      placeHolder: t('asset'),
      align: 'center',
      width: 950,
      headerAlign: 'center'
    },
  ];

  const showModal = (maintenanceId: number) => {
    setSelectedLocationId(maintenanceId);
    setIsModalVisible(true);
    getAssetsByLocationId(maintenanceId);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  const getAssetsByLocationId = (maintenanceId: number, e?) => {
    const body = {
      pageSize: e?.pageSize ?? 10,
      page: e?.page ?? 1,
      maintenanceId: maintenanceId
    }
    HTTPService.POST(`/Dashboard/assetbylocations?maintenanceId=${maintenanceId}`, body)
      .then(response => {
        if (!response.data) return;
        setSelectedAssets(response.data);
      })
      .catch(error => {
        console.error('Error fetching assets:', error);
        toast.current?.show({
          severity: 'error',
          summary: t('errors'),
          detail: t('errorFetchingData'),
          life: 3000
        });
      });
  };

  const locationColumns: IMUIDataGridColumns[] = [
    {
      title: t('maintenanceName'),
      placeHolder: 'maintenanceName',
      key: 'maintenanceName',
      isSorting: false,
      width: 150,
      render: (campusName) => (
        <div style={{
          fontWeight: 'bold',
        }}>
          {campusName}
        </div>
      ),
    },
    {
      title: t('maintenanceType'),
      placeHolder: 'maintenanceType',
      key: 'maintenanceType',
      isSorting: false,
      width: 150,
      render: (type: string) => {
        switch (type) {
          case 'Periodic Maintenance':
            return 'Periyodik Bakım';
          case 'Fault Maintenance':
            return 'Arıza Bakımı';
          default:
            return type;
        }
      }
    },
    {
      title: t('maintenanceDate'),
      placeHolder: 'maintenanceDate',
      isSorting: false,
      key: 'maintenanceDate',
      width: 150,
      render: (date: string) => {
        const parsedDate = new Date(date);
        const day = String(parsedDate.getDate()).padStart(2, '0');
        const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
        const year = parsedDate.getFullYear();
        return `${day}/${month}/${year}`;
      }
    },
    {
      title: t('asset'),
      placeHolder: 'asset',
      isSorting: false,
      key: 'assetCount',
      render: (_, record) => {
        return (
          <AntTooltip title={t('preview')} placement="top">
            <Button
              size='small'
              icon={<EyeOutlined />}
              style={{ width: '60px', borderRadius: '70px' }}
              onClick={() => {
                showModal(record.maintenanceId);
              }}

            />
          </AntTooltip>
        );
      },
    }
  ];

  const countingColumns: IAltisDataGridColumns[] = [
    {
      title: t('Counting Job Name'),
      placeHolder: t('Counting Job Name'),
      key: 'countingJobName',
      inputType: 'input',
      width: 100
    },
    {
      title: t('Counting Start Date'),
      placeHolder: t('Counting Start Date'),
      key: 'startDate',
      inputType: 'operations',
      width: 150,
      render: (value) => {
        if (!value) {
          return '';
        }

        return new Date(value).toLocaleDateString('tr-TR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      }
    },
    {
      title: t('Counting Finish Date'),
      placeHolder: t('Counting Finish Date'),
      inputType: 'operations',
      key: 'finishDate',
      width: 150,
      render: (value) => {
        if (!value) {
          return '';
        }

        return new Date(value).toLocaleDateString('tr-TR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      }
    },
    {
      title: t('counting_progress'),
      placeHolder: t('counting_progress'),
      inputType: 'progress',
      width: 90,
      key: 'completionPercentage',
    },
  ];


  return (
    <>
      <Toast ref={toast} />
      {
        userInfo.licenceEndDates ? (
          new Date(userInfo.licenceEndDates) > new Date() ? (
            <Alert
              className='mb-4'
              message={t('Welcome to The Tag And Trace Assets System')}
              type='success'
              showIcon
            />
          ) : (
            <Alert
              className='mb-4'
              message={t('licence_end_time')}
              type='error'
              showIcon
            />
          )
        ) : null
      }


      <br></br><br></br>
      {userInfo.role === 2 || userInfo.role === 3 ? (
        <Row gutter={16} className="mb-4">
          <Col span={6} xs={24} sm={12} md={6} lg={6} xl={6}>
            <MuiCard className={classes.card}>
              <Box
                className={classes.iconBox}
                style={{
                  backgroundColor: '#ff8c00',
                  boxShadow: '0 8px 20px rgba(255, 140, 0, 0.5)',
                  transition: 'box-shadow 0.3s ease',
                  width: '80px',
                  height: '80px'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 12px 30px rgba(255, 140, 0, 0.7)')}
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '0 8px 20px rgba(255, 140, 0, 0.5)')}
              >
                <AccountCircle className={classes.icon} />
              </Box>
              <CardContent className={classes.content}>
                <Typography className={classes.label} style={{ marginTop: '-30px' }} >
                  {t("userCount")}
                </Typography>
                <Typography className={classes.value} style={{ fontSize: '40px' }}>
                  {dashboardDetails?.userCount} / {dashboardDetails?.userLimit}
                </Typography>
              </CardContent>
            </MuiCard>
          </Col>
          <Col span={6} xs={24} sm={12} md={6} lg={6} xl={6}>
            <MuiCard className={classes.card}>
              <Box
                className={classes.iconBox}
                style={{
                  backgroundColor: '#43a047',
                  boxShadow: '0 8px 20px rgba(56, 142, 60, 0.5)',
                  transition: 'box-shadow 0.3s ease',
                  width: '80px',
                  height: '80px'
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.boxShadow = '0 12px 30px rgba(56, 142, 60, 0.7)')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.boxShadow = '0 8px 20px rgba(56, 142, 60, 0.5)')
                }
              >
                <span className="anticon" style={{ fontSize: '35px' }}>📦</span>
              </Box>
              <CardContent className={classes.content}>
                <Typography className={classes.label} style={{ marginTop: '-30px' }}>
                  {t("itemmanage")}
                </Typography>
                <Typography className={classes.value} style={{ fontSize: '40px' }}>
                  {dashboardDetails?.assetCount} / {dashboardDetails?.assetLimit}
                </Typography>
              </CardContent>
            </MuiCard>
          </Col>
          <Col span={6} xs={24} sm={12} md={6} lg={6} xl={6}>
            <MuiCard className={classes.card}>
              <Box
                className={classes.iconBox}
                style={{
                  backgroundColor: '#48D1CC',
                  boxShadow: '0 8px 20px rgba(114, 46, 209, 0.5)',
                  transition: 'box-shadow 0.3s ease',
                  width: '80px',
                  height: '80px'
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.boxShadow = '0 12px 30px rgba(114, 46, 209, 0.7)')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.boxShadow = '0 8px 20px rgba(114, 46, 209, 0.5)')
                }
              >
                <span className="anticon" style={{ fontSize: '35px' }}>📋</span>
              </Box>
              <CardContent className={classes.content}>
                <Typography className={classes.label} style={{ marginTop: '-30px' }}>
                  {t("totalCountings")}
                </Typography>
                <Typography className={classes.value} style={{ fontSize: '40px' }}>
                  {dashboardDetails?.totalCountingCount}
                </Typography>
              </CardContent>
            </MuiCard>
          </Col>
          <Col span={6} xs={24} sm={12} md={6} lg={6} xl={6}>
            <MuiCard className={classes.card}>
              <Box
                className={classes.iconBox}
                style={{
                  backgroundColor: '#f5222d',
                  boxShadow: '0 8px 20px rgba(245, 34, 45, 0.5)',
                  width: '80px',
                  height: '80px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'box-shadow 0.3s ease',
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.boxShadow = '0 12px 30px rgba(245, 34, 45, 0.7)')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.boxShadow = '0 8px 20px rgba(245, 34, 45, 0.5)')
                }
              >
                <span className="anticon" style={{ fontSize: '35px' }}>🖨️</span>
              </Box>
              <CardContent className={classes.content}>
                <Typography className={classes.label} style={{ marginTop: '-30px' }}>
                  {t("printerCount")}
                </Typography>
                <Typography className={classes.value} style={{ fontSize: '40px' }}>
                  {dashboardDetails?.printerCount}
                </Typography>
              </CardContent>
            </MuiCard>
          </Col>
        </Row>
      ) : (
        <></>
      )}

      <Row gutter={[16, 16]}>
        {userInfo?.role !== 4 && (
          <Col xs={24} sm={12} md={12} lg={12} xl={12}>
            <MuiCard
              title={t("inventory_status_name")}
              style={{ marginTop: '5px', width: '100%', height: '100%' }}
            >
              <div
                style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  padding: '10px',
                }}
              >
                {t("inventory_status_name")}
              </div>
              {pieData && pieData.length > 0 ? (
                <div style={{ width: '700px', height: '400px', margin: '0 auto', padding: '20px' }}>
                  <ResponsivePie
                    data={pieData}
                    margin={{ top: 60, right: 70, bottom: 60, left: 60 }}
                    innerRadius={0.6}
                    padAngle={0.7}
                    cornerRadius={3}
                    colors={['#0077b6', '#0096c7', '#00b4d8', '#48cae4', '#90e0ef']}
                    borderWidth={1}
                    borderColor={{
                      from: 'color',
                      modifiers: [['darker', 0.5]],
                    }}
                    arcLinkLabelsSkipAngle={10}
                    arcLinkLabelsTextColor="#333333"
                    arcLinkLabelsThickness={2}
                    arcLinkLabelsColor={{
                      from: 'color',
                      modifiers: [['darker', 0.4]],
                    }}
                    arcLabelsTextColor={{
                      from: 'color',
                      modifiers: [['darker', 5]],
                    }}
                    arcLinkLabelsOffset={-5}
                    arcLinkLabelsDiagonalLength={10}
                    arcLinkLabelsStraightLength={-5}
                    legends={[
                      {
                        anchor: 'bottom',
                        direction: 'row',
                        justify: false,
                        translateX: -5,
                        translateY: 56,
                        itemsSpacing: 20,
                        itemWidth: 75,
                        itemHeight: 10,
                        itemTextColor: '#999',
                        itemDirection: 'left-to-right',
                        symbolSize: 10,
                        symbolShape: 'circle',
                        effects: [
                          {
                            on: 'hover',
                            style: {
                              itemTextColor: '#000',
                            },
                          },
                        ],
                      },
                    ]}
                  />
                </div>
              ) : (
                <div style={{ width: '700px', height: '400px', margin: '0 auto', padding: '20px' }}>
                  <ResponsivePie
                    data={[
                      { id: 'Statü A', label: 'Statü A', value: 40 },
                      { id: 'Statü B', label: 'Statü B', value: 30 },
                      { id: 'Statü C', label: 'Statü C', value: 30 },
                    ]}
                    margin={{ top: 60, right: 70, bottom: 60, left: 60 }}
                    innerRadius={0.6}
                    padAngle={0.7}
                    cornerRadius={3}
                    colors={['#0077b6', '#0096c7', '#00b4d8', '#48cae4', '#90e0ef']}
                    borderWidth={1}
                    borderColor={{
                      from: 'color',
                      modifiers: [['darker', 0.5]],
                    }}
                    arcLinkLabelsSkipAngle={10}
                    arcLinkLabelsTextColor="#333333"
                    arcLinkLabelsThickness={2}
                    arcLinkLabelsColor={{
                      from: 'color',
                      modifiers: [['darker', 0.4]],
                    }}
                    arcLabelsTextColor={{
                      from: 'color',
                      modifiers: [['darker', 5]],
                    }}
                    arcLinkLabelsOffset={-5}
                    arcLinkLabelsDiagonalLength={10}
                    arcLinkLabelsStraightLength={-5}
                    legends={[
                      {
                        anchor: 'bottom',
                        direction: 'row',
                        justify: false,
                        translateX: -5,
                        translateY: 56,
                        itemsSpacing: 20,
                        itemWidth: 75,
                        itemHeight: 10,
                        itemTextColor: '#999',
                        itemDirection: 'left-to-right',
                        symbolSize: 10,
                        symbolShape: 'circle',
                        effects: [
                          {
                            on: 'hover',
                            style: {
                              itemTextColor: '#000',
                            },
                          },
                        ],
                      },
                    ]}
                  />
                </div>
              )}
            </MuiCard>
          </Col>
        )}

        {userInfo?.role !== 4 && (
          <Col xs={24} sm={12} md={12} lg={12} xl={12}>
            <MuiCard style={{ marginTop: '5px', width: '100%', height: '100%' }}>
              <div
                style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  padding: '10px',
                }}
              >
                {t("category")}
              </div>
              <div style={{ width: '100%', height: '400px', marginLeft: '30px' }}>
                <BarChart
                  width={700}
                  height={450}
                  data={categoryChartData}
                  margin={{
                    top: 20,
                    bottom: 50
                  }}
                >
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={70}
                    interval={0}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis hide={true} />
                  <Tooltip
                    formatter={(value) => [`${value} adet`, 'Miktar']}
                    labelStyle={{ color: '#666' }}
                  />
                  <Bar
                    dataKey="value"
                    shape={(props) => {
                      const { x, y, width, height, value } = props;
                      const radius = 5;
                      return (
                        <g>
                          <rect
                            x={x}
                            y={y}
                            width={width}
                            height={height}
                            fill={props.fill}
                            rx={radius}
                            ry={radius}
                          />
                          <text
                            x={x + width / 2}
                            y={y - 5}
                            textAnchor="middle"
                            fill="#666"
                            fontSize={12}
                          >
                            {value}
                          </text>
                        </g>
                      );
                    }}
                  >
                    {categoryChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </div>
            </MuiCard>
          </Col>
        )}
      </Row>


      <Row gutter={[16, 16]} style={{ marginTop: '45px' }}>
        {/* <Col span={12}>
          <MuiCard
            title={t("countingJobsNow")}
            style={{ marginTop: '-20px', width: '100%'}}
          >
              <div
                style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  padding: '10px',
                }}
              >
                {t("countingJobsNow")}
              </div>
            {activeCountings && activeCountings.length > 0 ? (
              <div style={{ width: '90%', margin: '0 auto', padding: '20px' }}>
              {activeCountings.map((counting, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <div
                    style={{
                      width: '200px',
                      marginRight: '15px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    <strong><label>{t('Counting Job Name')}</label></strong>
                    <br></br>
                    <label>{counting.name}</label>
                    <div style={{ fontSize: '11px', color: '#666' }}>
                      <strong style={{ fontSize: '11px' }}>{`${counting.startDate} - ${counting.finishDate}`}</strong>
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <ProgressBar
                      completed={counting.percent}
                      bgColor="#76c76b"
                      height="15px"
                      labelAlignment="outside"
                      labelColor="#666"
                      baseBgColor="grey"
                      borderRadius="4px"
                      />
                  </div>
                </div>
              ))}
            </div>
            ) : (
              <p style={{ textAlign: 'center', marginTop: '20px' }}>{t("noData")}</p>
            )}
          </MuiCard>
        </Col> */}

        {/* <Col span={12}>
          <MuiCard
            title={t("futureCountingJobs")}
            style={{width: '100%',marginTop:'-20px' }}
          >
              <div
                style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  padding: '10px',
                }}
              >
                {t("futureCountingJobs")}
              </div>
            {futureCountings && futureCountings.length > 0 ? (
              <div style={{ width: '90%', margin: '0 auto', padding: '20px' }}>
                {futureCountings.map((counting, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: '20px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <div
                      style={{
                        width: '200px',
                        marginRight: '15px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      <strong><label>{t('Counting Job Name')}</label></strong>
                      <br></br>
                      <label>{counting.name}</label>
                      <div style={{ fontSize: '11px', color: '#666' }}>
                        <strong style={{ fontSize: '11px' }}>{`${counting.startDate} - ${counting.finishDate}`}</strong>
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <ProgressBar
                        completed={counting.percent}
                        bgColor="#76c76b"
                        height="15px"
                        labelAlignment="outside"
                        labelColor="#666"
                        baseBgColor="grey"
                        borderRadius="4px"
                        />
                      <div
                        style={{
                          fontSize: '11px',
                          color: '#666',
                          marginTop: '4px',
                        }}
                      >
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ textAlign: 'center', marginTop: '20px' }}>{t("noData")}</p>
            )}
          </MuiCard>
        </Col> */}
      </Row>

      {userInfo?.role !== 4 && (
        <Row gutter={[8, 8]} style={{ marginTop: '-20px' }}>
          <MuiCard
            title={t('activeandwaitcount')}
            style={{
              marginTop: '-5px',
              width: '99.5%',
              height: '100%',
              margin: '0 auto',
            }}
          >
            <div
              style={{
                fontSize: '18px',
                fontWeight: 'bold',
                textAlign: 'center',
                padding: '10px',
              }}
            >
              {t("activeandwaitcount")}
            </div>
            <div style={{
              marginTop: '10px',
              marginBottom: '40px',
              marginLeft: '40px',
              marginRight: '40px',
            }}>
              {allCounting?.count !== 0 && (
                <AltisDataGrid
                  ref={dataGridRef}
                  columns={countingColumns}
                  data={allCounting?.data || []}
                  total={allCounting?.count}
                  onChange={() => { }}
                />
              )}
            </div>
          </MuiCard>
        </Row>
      )}


      <Row gutter={[16, 16]} style={{ display: 'flex', alignItems: 'stretch', marginTop: '20px' }}>
        {userInfo?.role !== 4 && (
          <Col xs={24} sm={12} md={12} lg={12} xl={12} style={{ marginBottom: '20px' }}>
            <MuiCard
              title={t("assetbyCampus")}
              style={{
                marginTop: '15px',
                width: '100%',
                height: '750px',
                margin: '0 auto'
              }}
            >
              <div
                style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  padding: '10px',
                }}
              >
                {t("assetbyCampus")}
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%'
                }}
              >
                {campusData?.data?.length > 0 ? (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    maxWidth: '400px',
                    maxHeight: '400px',
                    margin: '0 auto'
                  }}>
                    <ResponsivePie
                      data={campusData.data.filter(row => row.campusCount > 0).map(item => ({
                        id: item.campusName,
                        label: item.campusName,
                        value: item.campusCount,
                      }))}
                      margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                      innerRadius={0}
                      padAngle={1}
                      cornerRadius={3}
                      colors={{ scheme: 'nivo' }}
                      borderWidth={1}
                      borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                      arcLinkLabelsSkipAngle={10}
                      arcLinkLabelsTextOffset={1}
                      arcLinkLabelsTextColor="#333333"
                      arcLinkLabelsDiagonalLength={10}
                      arcLinkLabelsStraightLength={6}
                      arcLinkLabelsThickness={2}
                      arcLinkLabelsColor={{ from: 'color' }}
                      arcLabelsSkipAngle={10}
                      arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                      legends={[{
                        anchor: 'bottom',
                        direction: 'row',
                        justify: false,
                        translateX: -5,
                        translateY: 56,
                        itemsSpacing: 15,
                        itemWidth: 60,
                        itemHeight: 18,
                        itemTextColor: '#999',
                        itemDirection: 'top-to-bottom',
                        symbolSize: 9,
                        symbolShape: 'circle',
                        effects: [{
                          on: 'hover',
                          style: {
                            itemTextColor: '#000',
                          },
                        }],
                      }]}
                      theme={{
                        legends: {
                          text: {
                            fontSize: 9,
                          },
                        },
                      }}
                    />
                  </div>
                ) : (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    maxWidth: '400px',
                    maxHeight: '400px',
                    margin: '0 auto'
                  }}>
                    <ResponsivePie
                      data={[
                        { id: 'Kampüs A', label: 'Type A', value: 50 },
                        { id: 'Kampüs B', label: 'Type B', value: 30 },
                        { id: 'Kampüs C', label: 'Type C', value: 20 },
                      ]}
                      margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                      innerRadius={0}
                      padAngle={1}
                      cornerRadius={3}
                      colors={{ scheme: 'nivo' }}
                      borderWidth={1}
                      borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                      arcLinkLabelsSkipAngle={10}
                      arcLinkLabelsTextOffset={1}
                      arcLinkLabelsTextColor="#333333"
                      arcLinkLabelsDiagonalLength={10}
                      arcLinkLabelsStraightLength={6}
                      arcLinkLabelsThickness={2}
                      arcLinkLabelsColor={{ from: 'color' }}
                      arcLabelsSkipAngle={10}
                      arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                      legends={[{
                        anchor: 'bottom',
                        direction: 'row',
                        justify: false,
                        translateX: -5,
                        translateY: 56,
                        itemsSpacing: 15,
                        itemWidth: 60,
                        itemHeight: 18,
                        itemTextColor: '#999',
                        itemDirection: 'top-to-bottom',
                        symbolSize: 9,
                        symbolShape: 'circle',
                        effects: [{
                          on: 'hover',
                          style: {
                            itemTextColor: '#000',
                          },
                        }],
                      }]}
                      theme={{
                        legends: {
                          text: {
                            fontSize: 9,
                          },
                        },
                      }}
                    />
                  </div>
                )}
              </div>
            </MuiCard>
          </Col>
        )}


        {userInfo?.role !== 4 && (
          <Col xs={24} sm={12} md={12} lg={12} xl={12}>
            <MuiCard
              title={t("maintenanceandfail")}
              style={{
                marginTop: '-5px',
                width: '100%',
                height: '750px',
                margin: '0 auto',
              }}
            >
              <div
                style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  padding: '10px',
                }}
              >
                {t("maintenanceandfail")}
              </div>
              <div
                style={{
                  filter: userInfo?.licencePackage?.isMaintenance ? 'none' : 'blur(4px)',
                  pointerEvents: userInfo?.licencePackage?.isMaintenance ? 'auto' : 'none',
                  position: 'relative'
                }}
              >
                <MUIDatagrid
                  columns={locationColumns}
                  data={assetLocationDetail?.data?.length ? assetLocationDetail.data : []}
                  total={assetLocationDetail?.count}
                  onChange={(e) => AssetLocationFilter(e)}
                />
                {!userInfo?.licencePackage?.isMaintenance && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: 'rgba(255,255,255,0.6)',
                      color: '#333',
                      fontWeight: 'bold',
                      zIndex: 1,
                    }}
                  >
                    {t('maintenanceModuleAuthorization')}
                  </div>
                )}
              </div>
            </MuiCard>
          </Col>
        )}
      </Row>
      <Modal
        title={t('assetDetails')}
        open={isModalVisible}
        onCancel={handleModalClose}
        width={1000}
        footer={null}
      >
        <MUIDatagrid
          //ref={dataGridRef}
          columns={assetModalColumns}
          defaultPageSize={10}
          data={selectedAssets?.data}
          total={selectedAssets?.count}
          onChange={(e) => selectedLocationId && getAssetsByLocationId(selectedLocationId, e)}
        />
      </Modal>
    </>
  );

};

export default Dashboard;